import React, { useEffect, useRef, useState } from 'react'
import { createSpeechSynthesisPonyfill } from '@davi-ai/web-speech-cognitive-services-davi'
import type { SpeechSynthesisPonyfillType, PonyfillOptions } from '@davi-ai/web-speech-cognitive-services-davi'

import { useLocaleStore } from '../Contexts/LocaleContext'
import { useSpeechStore } from '../Contexts/SpeechContext'
import { useRetorikStore } from '../Contexts/RetorikContext'

import { fetchSpeechServicesToken } from '../../utils/fetchSpeechServicesToken'
import { selectVoice } from '../../utils/selectVoice'

import SynthesisManager from './SynthesisManager'

interface SynthesisPonyfillManagerProps {
  isRetorikNews?: boolean
}

const SynthesisPonyfillManager = ({ isRetorikNews }: SynthesisPonyfillManagerProps): JSX.Element | null => {
  const customVoice = useSpeechStore((state) => state.customVoice)
  const ponyfillCredentials = useSpeechStore((state) => state.ponyfillCredentials)
  const locale = useLocaleStore((state) => state.locale)
  const agentData = useRetorikStore((state) => state.agentData)
  const setVoice = useSpeechStore((state) => state.actions.setVoice)

  const [ponyfillCreated, setPonyfillCreated] = useState<boolean>(false)
  const ponyfillRef = useRef<SpeechSynthesisPonyfillType | undefined>(undefined)
  const refreshTimerRef = useRef<NodeJS.Timeout | null>(null)

  /**
   * Async function to retrieve a new token and assign it to the speechConfig inside ponyfill.speechSynthesis
   * @param {string} region
   * @param {string} key
   */
  const refreshTokenInNineMinutes = async (region: string, key: string): Promise<void> => {
    // A token is valid during 10 minutes, let's refresh it after 9 minutes
    refreshTimerRef.current = setTimeout(async () => {
      const token = await fetchSpeechServicesToken(region, key)

      if (token && ponyfillRef.current?.speechSynthesis?.speechConfig) {
        ponyfillRef.current.speechSynthesis.speechConfig.authorizationToken = token
        refreshTokenInNineMinutes(region, key)
      }
    }, 1000 * 60 * 9) // 9 minutes
  }

  /**
   * Fetch speech synthesis token and create the ponyfill containing all data
   * @param {string} region
   * @param {string} key
   */
  const fetchTokenAndCreatePonyfill = async (region: string, key: string): Promise<void> => {
    const token = await fetchSpeechServicesToken(region, key)

    if (token) {
      ponyfillRef.current = createSpeechSynthesisPonyfill({
        credentials: {
          region: ponyfillCredentials?.region,
          authorizationToken: token
        }
      })
      // Launch token refresh after ponyfill creation
      refreshTokenInNineMinutes(region, key)
      setPonyfillCreated(true)
    }
  }

  useEffect(() => {
    if (ponyfillCredentials?.authorizationToken && ponyfillCredentials.region) {
      const data: PonyfillOptions = {
        credentials: ponyfillCredentials
      }
      ponyfillRef.current = createSpeechSynthesisPonyfill(data)
      setPonyfillCreated(true)
    } else if (ponyfillCredentials?.subscriptionKey && ponyfillCredentials.region) {
      fetchTokenAndCreatePonyfill(ponyfillCredentials.region, ponyfillCredentials.subscriptionKey)
    } else if (ponyfillCredentials?.speechSynthesisHostname) {
      // To use self-hosted voice containers, we need no 'region', and 'speechSynthesisHostname' and 'speechRecognitionHostname' must both be defined
      // As this ponyfill is for speech synthesis only, we put a mock 'speechRecognitionHostname' 
      const data: PonyfillOptions = {
        credentials: {
          // @ts-ignore
          speechSynthesisHostname: new URL(ponyfillCredentials.speechSynthesisHostname),
          speechRecognitionHostname: ponyfillCredentials.speechRecognitionHostname || 'mandatory_recognition_host_name',
          subscriptionKey: ponyfillCredentials.subscriptionKey || 'mandatory_subscription_key'
        }
      }
      ponyfillRef.current = createSpeechSynthesisPonyfill(data)
      setPonyfillCreated(true)
    }
  }, [ponyfillCredentials])

  const onVoicesChanged = (): void => {
    const voices = ponyfillRef.current?.speechSynthesis.getVoices()
    if (voices && Array.isArray(voices) && voices.length) {
      setVoice(selectVoice(voices, locale, customVoice, agentData))
    }
  }

  useEffect(() => {
    if (ponyfillRef.current) {
      const voices = ponyfillRef.current.speechSynthesis.getVoices()
      if (voices && Array.isArray(voices) && voices.length) {
        setVoice(selectVoice(voices, locale, customVoice, agentData))
      } else {
        ponyfillRef.current.speechSynthesis.onvoiceschanged = onVoicesChanged
      }
    }
  }, [ponyfillCreated, customVoice, agentData, locale])

  useEffect(() => {
    return (): void => {
      refreshTimerRef.current && clearTimeout(refreshTimerRef.current)
    }
  }, [])

  return ponyfillCreated && ponyfillRef.current ? <SynthesisManager ponyfill={ponyfillRef.current} isRetorikNews={isRetorikNews} /> : <React.Fragment />
}

export default SynthesisPonyfillManager
